import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../hooks/useAuth';

interface TenantSummary {
  id: string;
  plan: string;
  interactionsUsed: number;
  interactionsLimit: number;
  createdAt: string;
  userCount: number;
  instagramAccountCount: number;
}

interface AdminStats {
  totalTenants: number;
  totalUsers: number;
  totalInstagramAccounts: number;
  usersByRole: Record<string, number>;
  tenantsByPlan: Record<string, number>;
}

export const Admin = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [tenantsRes, statsRes] = await Promise.all([
          api.get<{ tenants: TenantSummary[] }>('/api/admin/tenants'),
          api.get<AdminStats>('/api/admin/stats'),
        ]);
        setTenants(tenantsRes.tenants);
        setStats(statsRes);
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Erreur de chargement');
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const onLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-xl font-semibold text-neutral-900">
              Falko
            </Link>
            <span className="text-xs uppercase tracking-wider text-violet-600 bg-violet-50 px-2 py-1 rounded-full">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Link
              to="/dashboard"
              className="text-neutral-600 hover:text-neutral-900 px-3 py-1.5 rounded-lg hover:bg-neutral-100 transition"
            >
              Tableau de bord
            </Link>
            <button
              onClick={onLogout}
              className="text-neutral-600 hover:text-neutral-900 px-3 py-1.5 rounded-lg hover:bg-neutral-100 transition"
            >
              Se déconnecter
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-neutral-900">Back-office</h2>
          <p className="text-sm text-neutral-500 mt-1">
            Connectée en tant que <strong>{user?.email}</strong> · {user?.role}
          </p>
        </div>

        {isLoading && <p className="text-neutral-500">Chargement…</p>}

        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>
        )}

        {!isLoading && !error && stats && (
          <>
            <section className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <StatCard label="Tenants" value={stats.totalTenants} />
              <StatCard label="Utilisateurs" value={stats.totalUsers} />
              <StatCard label="Comptes Instagram" value={stats.totalInstagramAccounts} />
            </section>

            <section className="bg-white rounded-2xl shadow-sm border border-neutral-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-neutral-200">
                <h3 className="text-lg font-semibold text-neutral-900">Tenants ({tenants.length})</h3>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 text-left text-neutral-500 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="px-6 py-3">ID</th>
                    <th className="px-6 py-3">Plan</th>
                    <th className="px-6 py-3">Utilisateurs</th>
                    <th className="px-6 py-3">Comptes IG</th>
                    <th className="px-6 py-3">Interactions</th>
                    <th className="px-6 py-3">Créé le</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {tenants.map((t) => (
                    <tr key={t.id} className="hover:bg-neutral-50">
                      <td className="px-6 py-3 font-mono text-xs text-neutral-700">{t.id.substring(0, 8)}…</td>
                      <td className="px-6 py-3">
                        <span className="inline-block bg-violet-50 text-violet-700 text-xs px-2 py-0.5 rounded-full">
                          {t.plan}
                        </span>
                      </td>
                      <td className="px-6 py-3">{t.userCount}</td>
                      <td className="px-6 py-3">{t.instagramAccountCount}</td>
                      <td className="px-6 py-3">
                        {t.interactionsUsed} / {t.interactionsLimit}
                      </td>
                      <td className="px-6 py-3 text-neutral-500">
                        {new Date(t.createdAt).toLocaleDateString('fr-FR')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          </>
        )}
      </main>
    </div>
  );
};

const StatCard = ({ label, value }: { label: string; value: number }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 px-6 py-4">
    <p className="text-xs uppercase tracking-wider text-neutral-500">{label}</p>
    <p className="text-3xl font-semibold text-neutral-900 mt-1">{value}</p>
  </div>
);
